import SiteHeader from "../../components/feature/SiteHeader";
import SiteFooter from "../../components/feature/SiteFooter";
import PageHero from "../../components/feature/PageHero";
import usePageTitle from "../../hooks/usePageTitle";
import { aboutCover, site } from "../../mocks/site";
import ContactForm from "./components/ContactForm";

const principles = [
  { icon: "ri-flag-2-line", title: "讲清楚", desc: "分集剧情按序梳理，人物、设定、结局一五一十，让新观众也能快速上手。" },
  { icon: "ri-search-line", title: "讲准确", desc: "坚持查证原著与动画，避免道听途说，让每一条剧情信息都有据可循。" },
  { icon: "ri-heart-3-line", title: "讲得暖", desc: "用真诚和共情去理解角色与故事，把守护甜心最温柔的那一面传递出来。" },
];

const testimonials = [
  { quote: "终于不用在弹幕里翻剧情了，这里从第一集开始按顺序讲，补番特别顺。", from: "一位老观众" },
  { quote: "几斗和亚梦那篇解析写到我心里去了，看完又去把结局重刷了一遍。", from: "匿名读者" },
  { quote: "分集速览简洁但不敷衍，设定解析也很清楚，会持续追更。", from: "甜心迷" },
];

export default function About() {
  usePageTitle("关于本站");

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于甜心剧情站",
    description: site.description,
    url: "https://sequelio.github.io/about",
    inLanguage: "zh-CN",
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <SiteHeader />
      <main>
        <PageHero
          crumbs={[{ label: "首页", path: "/" }, { label: "关于本站" }]}
          title="关于本站"
          subtitle="甜心剧情站是一群守护甜心老观众自发整理的剧情专题站，专注分集剧情、剧情解析与结局详解。"
          badge="认识我们"
        />

        <section className="mx-auto max-w-7xl px-4 md:px-6 py-8">
          <div className="relative overflow-hidden rounded-3xl">
            <img src={aboutCover} alt="甜心剧情站的创作与整理场景" className="h-72 w-full object-cover object-top" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-900/75 to-transparent" />
            <div className="absolute inset-x-6 bottom-6 max-w-xl">
              <h2 className="font-display text-2xl text-background-50">和你一样，认真对待每一个故事</h2>
              <p className="mt-2 text-sm text-background-50/90">
                {site.slogan}
              </p>
            </div>
          </div>

          <div className="mx-auto mt-14 max-w-3xl">
            <h2 className="font-display text-2xl text-foreground-950 md:text-3xl">我们是谁，为什么做这个站</h2>
            <p className="mt-4 text-base leading-8 text-foreground-700">
              {site.name} 的初衷很简单：守护甜心是很多人的童年回忆，但它的分集剧情分散、结局线索交错，
              想重刷时常常要来回翻找。所以我们把全部分集剧情按顺序梳理成速览，把角色、设定与结局逐层拆解，
              让每个想重温这个故事的人，都能一进来就对上号、看得懂。
            </p>
            <p className="mt-4 text-base leading-8 text-foreground-700">
              我们不是官方，只是一群真心喜欢守护甜心的整理者。内容坚持客观、准确、温暖，
              也欢迎每一位观众一起补充和纠错。
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
            {principles.map((p) => (
              <div key={p.title} className="rounded-2xl border border-background-200/70 bg-background-50 p-6">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-100 text-2xl text-primary-700">
                  <i className={p.icon} />
                </span>
                <h3 className="mt-4 font-display text-xl text-foreground-950">{p.title}</h3>
                <p className="mt-2 text-sm leading-6 text-foreground-700">{p.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 rounded-3xl bg-background-100 p-6 md:p-10">
            <h2 className="text-center font-display text-2xl text-foreground-950 md:text-3xl">观众怎么说</h2>
            <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
              {testimonials.map((t) => (
                <figure key={t.from} className="rounded-2xl border border-background-200/70 bg-background-50 p-6">
                  <span className="flex text-accent-500"><i className="ri-chat-quote-line text-2xl" /></span>
                  <blockquote className="mt-3 text-sm leading-7 text-foreground-700">「{t.quote}」</blockquote>
                  <figcaption className="mt-4 text-xs text-foreground-600">— {t.from}</figcaption>
                </figure>
              ))}
            </div>
          </div>

          <div className="mx-auto mt-14 max-w-3xl">
            <h2 className="font-display text-2xl text-foreground-950 md:text-3xl">联系我们</h2>
            <p className="mt-3 text-sm leading-6 text-foreground-700">
              剧情纠错、内容投稿、合作建议，都欢迎通过下面的表单或邮箱与我们联系。
            </p>
            <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-2">
              <div className="rounded-2xl border border-background-200/70 bg-background-50 p-6">
                <h3 className="font-medium text-foreground-950">留下你的话</h3>
                <div className="mt-4">
                  <ContactForm />
                </div>
              </div>
              <div className="space-y-4">
                <div className="rounded-2xl border border-background-200/70 bg-background-50 p-6">
                  <h3 className="font-medium text-foreground-950">邮件联系</h3>
                  <a
                    href={`mailto:${site.email}`}
                    className="mt-3 inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
                  >
                    <i className="ri-mail-line" />
                    {site.email}
                  </a>
                </div>
                <div className="rounded-2xl border border-background-200/70 bg-background-50 p-6">
                  <h3 className="font-medium text-foreground-950">常逛栏目</h3>
                  <ul className="mt-3 space-y-2 text-sm text-foreground-700">
                    <li>· 分集剧情速览，从第一集按序梳理</li>
                    <li>· 角色 / 设定 / 结局深度解析</li>
                    <li>· 新手补番观看指南</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}